interface Subscription {
  end_date: string;
  id: string;
  plan: string;
  start_date: string;
  status: string;
  cycle_type: string;
  stripe_subscription_id: string;
  user_id: string;
  stripe_customer_id: string;
  created_at: string;
  updated_at: string;
}

interface Invoice {
  id: string;
  user_id: string;
  user_stripe_email: string;
  subscription_id: string;
  stripe_invoice_id: string;
  stripe_customer_id: string;
  stripe_billing_reason: string;
  stripe_payment_intent_id: string;
  stripe_subscription_id: string;
  amount: number;
  currency: string;
  status: string;
  attempt_count: number;
  next_payment_attempt: string;
  due_date: string;
  paid_date: string;
  invoice_url: string;
  created_at: string;
  updated_at: string;
}

interface PaymentIntent {
  id: string;
  user_id: string;
  stripe_customer_id: string;
  subscription_id: string;
  stripe_subscription_id: string;
  stripe_payment_intent_id: string;
  stripe_invoice_id: string;
  stripe_created_at: string;
  stripe_description: string;
  status: string;
  currency: string;
  payment_method: string;
  failed_reason: string;
  failure_decline_code: string;
  created_at: string;
  updated_at: string;
}

interface CheckoutSession {
  checkout_session_id: string;
  stripe_subscription_id: string;
  subscription_id: string;
  url: string;
  user_id: string;
}

/** Matches backend `constants.StripeNotSubscribedCustomerID`. */
const StripePlaceholderCustomerId = "cus-not-subscribed" as const;

/** Stripe billing portal needs a real Customer; trials use `cus-not-subscribed`. */
function subscriptionHasStripeBillingPortal(subscription: Subscription): boolean {
  if (subscriptionIsFreeTrialTrainee(subscription)) {
    return false;
  }
  return (
    subscription.stripe_customer_id !== "" &&
    subscription.stripe_customer_id !== StripePlaceholderCustomerId
  );
}

interface BillingPortalSession {
  billing_portal_session_id: string;
  subscription_id: string;
  stripe_customer_id: string;
  user_id: string;
  url: string;
}

const SubscriptionStates = {
  Active: "active",
  Pending: "pending",
  Expired: "expired",
  Paused: "paused",
  Cancelled: "cancelled",
} as const;

const SubscriptionCycleTypes = {
  Monthly: "monthly",
  Yearly: "yearly",
} as const;

const SubscriptionPlans = {
  FreeTrial: {
    Trainee: "free-trial-trainee",
  },
  Premium: {
    Trainee: "premium-trainee",
  },
} as const;

type SubscriptionPlansOptions =
  | typeof SubscriptionPlans.FreeTrial.Trainee
  | typeof SubscriptionPlans.Premium.Trainee;

function subscriptionIsFreeTrialTrainee(subscription: Subscription): boolean {
  return subscription.plan === SubscriptionPlans.FreeTrial.Trainee;
}

export type {
  Subscription,
  Invoice,
  CheckoutSession,
  BillingPortalSession,
  SubscriptionPlansOptions,
  PaymentIntent,
};
export {
  SubscriptionStates,
  SubscriptionPlans,
  SubscriptionCycleTypes,
  StripePlaceholderCustomerId,
  subscriptionHasStripeBillingPortal,
};
